<!-- Unified Command Palette Overlay -->
<div class="palette-overlay" *ngIf="isOpen" (click)="close()">
  <div class="palette-container" [class.trial-mode]="isTrialMode" (click)="$event.stopPropagation()">

    <!-- ═══════════════════════════════════════════════════════════════════════════ -->
    <!-- TRIAL MODE: Onboarding Progress Header -->
    <!-- ═══════════════════════════════════════════════════════════════════════════ -->
    <div class="onboarding-header" *ngIf="isTrialMode && onboardingProgress">
      <div class="onboarding-header-content">
        <div class="onboarding-title">
          <mat-icon class="onboarding-icon">rocket_launch</mat-icon>
          <span class="onboarding-text">Setup Progress</span>
        </div>
        <div class="onboarding-stats">
          <span class="stats-text">{{ onboardingProgressText }} completed</span>
          <div class="progress-bar-container">
            <div class="progress-bar-fill" [style.width.%]="onboardingPercent"></div>
          </div>
          <span class="stats-percent">{{ onboardingPercent }}%</span>
        </div>
      </div>
    </div>

    <!-- Header with Search and Support Button -->
    <div class="palette-header">
      <div class="palette-search">
        <span class="search-icon">🔍</span>
        <input
          #searchInput
          type="text"
          class="search-input"
          placeholder="Search or ask anything..."
          [(ngModel)]="query"
          (ngModelChange)="onQueryChange($event)"
          autocomplete="off"
          spellcheck="false"
        />
        <span
          *ngIf="query"
          class="clear-icon"
          title="Clear"
          (click)="clearQuery()"
        >✕</span>
      </div>

      <!-- Search / Chat toggle based on query state -->
      <button
        *ngIf="query.trim().length >= 3 && !hasSearched"
        class="search-button"
        (click)="triggerSearch()"
        title="Search (Enter)"
      >
        🔍 Search
      </button>
      <button
        *ngIf="query.trim().length < 3 || hasSearched"
        class="support-button"
        (click)="openSupportChat()"
        title="Chat with Support"
      >
        💬 Chat with Us
      </button>
    </div>

    <!-- Related Search Chips -->
    <!-- Loading State -->
    <div class="palette-loading" *ngIf="isLoading">
      <span class="loading-spinner">⏳</span>
      <span class="loading-text">Searching...</span>
    </div>

    <!-- 2-Pane Layout: Main Content + Quick Actions Sidebar -->
    <div class="palette-body" *ngIf="!isLoading">

      <!-- Left Pane: Main Content -->
      <div class="palette-main-content" [class.has-onboarding]="isTrialMode">

        <!-- ═══════════════════════════════════════════════════════════════════════════ -->
        <!-- TRIAL MODE: Onboarding Checklist (replaces discovery when no query) -->
        <!-- ═══════════════════════════════════════════════════════════════════════════ -->
        <div class="onboarding-section" *ngIf="isTrialMode && !query.trim() && onboardingSteps.length > 0">

          <!-- All Complete Celebration -->
          <div class="onboarding-complete" *ngIf="onboardingPercent === 100">
            <div class="celebration-content">
              <div class="celebration-icon">🎉</div>
              <div class="celebration-title">You're all set!</div>
              <div class="celebration-subtitle">
                Great job completing your setup. You're ready to make the most of your account.
              </div>
            </div>
          </div>

          <!-- Tier Sections -->
          <div
            *ngFor="let tier of visibleTiers"
            class="onboarding-tier"
            [class.tier-complete]="isTierComplete(tier)"
            [class.tier-active]="onboardingProgress?.currentTier === tier"
            [attr.data-tier]="tier"
          >
            <!-- Tier Header -->
            <div class="tier-header">
              <div class="tier-header-left">
                <span class="tier-emoji">{{ tierConfig[tier].emoji }}</span>
                <span class="tier-label">{{ tierConfig[tier].label }}</span>
              </div>
              <div class="tier-header-right">
                <span class="tier-progress-text">
                  {{ getTierProgress(tier).completed }}/{{ getTierProgress(tier).total }}
                </span>
                <mat-icon class="tier-check" *ngIf="isTierComplete(tier)">check_circle</mat-icon>
              </div>
            </div>

            <!-- Steps List -->
            <div class="onboarding-steps">
              <div
                *ngFor="let step of getStepsForTier(tier); trackBy: trackByStepId"
                class="onboarding-step"
                [class.completed]="isStepCompleted(step)"
                [class.active]="isStepActive(step)"
                (click)="!isStepCompleted(step) && startOnboardingStep(step)"
              >
                <!-- Step Status Icon -->
                <div class="step-status">
                  <mat-icon *ngIf="isStepCompleted(step)" class="status-complete">check_circle</mat-icon>
                  <mat-icon *ngIf="isStepActive(step) && !isStepCompleted(step)" class="status-active">sync</mat-icon>
                  <div *ngIf="!isStepCompleted(step) && !isStepActive(step)" class="status-pending">
                    <mat-icon class="step-icon">{{ getStepIcon(step) }}</mat-icon>
                  </div>
                </div>

                <!-- Step Content -->
                <div class="step-content">
                  <div class="step-title">{{ getStepTitle(step) }}</div>
                  <div class="step-description" *ngIf="!isStepCompleted(step)">
                    {{ getStepDescription(step) }}
                  </div>
                </div>

                <!-- Step Actions -->
                <div class="step-actions" *ngIf="!isStepCompleted(step)">
                  <span class="step-time" *ngIf="step.config.estimatedMinutes">
                    ~{{ step.config.estimatedMinutes }}m
                  </span>
                  <button
                    class="step-cta-btn"
                    (click)="startOnboardingStep(step); $event.stopPropagation()"
                  >
                    {{ getStepCta(step) }}
                    <mat-icon class="cta-arrow">arrow_forward</mat-icon>
                  </button>
                </div>

                <!-- Completed Badge -->
                <div class="step-complete-badge" *ngIf="isStepCompleted(step)">
                  <span>Done</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <!-- End Onboarding Section -->

        <!-- ═══════════════════════════════════════════════════════════════════════════ -->
        <!-- NORMAL MODE: Discovery/Suggestions (shown when query or not trial) -->
        <!-- ═══════════════════════════════════════════════════════════════════════════ -->
        <div class="results-section discovery-section" *ngIf="(!isTrialMode || query.trim()) && (results.discovery.length > 0 || results.suggestions.length > 0)">
          <div class="section-header">
            <span class="section-icon">✨</span>
            <span class="section-title">
              {{ !query.trim() ? 'Things you can do on ' + (results.routeName || 'this') + ' page' : 'Suggestions' }}
            </span>
          </div>
          <div class="section-items">
            <!-- Discovery items (when no query) -->
            <div
              *ngFor="let feature of results.discovery; let i = index; trackBy: trackById"
              class="palette-item discovery-item"
              [class.selected]="selectedSection === 'discovery' && i === selectedIndex"
              (click)="executeDiscovery(feature)"
              (mouseenter)="selectItem('discovery', i)"
            >
              <div class="item-icon">
                <mat-icon *ngIf="feature.icon; else featureIconFallback" [fontIcon]="feature.icon" aria-hidden="true"></mat-icon>
                <ng-template #featureIconFallback>
                  <span class="item-icon-text">★</span>
                </ng-template>
              </div>
              <div class="item-content">
                <div class="item-title">{{ feature.title }}</div>
                <div class="item-subtitle">{{ feature.description }}</div>

                <!-- Action buttons -->
                <div class="discovery-actions">
                  <button
                    *ngIf="feature.workflowId"
                    class="discovery-action-btn"
                    (click)="$event.stopPropagation(); executeDiscovery(feature)"
                    title="Launch interactive guide"
                  >
                    ▶ Show Me
                  </button>
                  <button
                    *ngIf="feature.videoUrl"
                    class="discovery-action-btn"
                    (click)="$event.stopPropagation(); toggleDiscoveryVideo(feature)"
                    title="Watch video tutorial"
                  >
                    📺 {{ expandedDiscoveryVideoId === feature.id ? 'Hide' : 'Watch' }}
                  </button>
                </div>

                <!-- Inline Video Player for discovery items -->
                <div class="video-player" *ngIf="expandedDiscoveryVideoId === feature.id">
                  <video
                    controls
                    autoplay
                    preload="metadata"
                    [src]="expandedDiscoveryVideoUrl"
                    (click)="$event.stopPropagation()"
                  >
                    Your browser does not support the video tag.
                  </video>
                </div>
              </div>
            </div>

            <!-- Suggestion items (when query exists) -->
            <div
              *ngFor="let suggestion of results.suggestions; let i = index; trackBy: trackById"
              class="palette-item suggestion-item"
              [class.selected]="selectedSection === 'suggestions' && i === selectedIndex"
              (click)="executeSuggestion(suggestion)"
              (mouseenter)="selectItem('suggestions', i)"
            >
              <div class="item-icon">
                <mat-icon *ngIf="suggestion.icon; else suggestionIconFallback" [fontIcon]="suggestion.icon" aria-hidden="true"></mat-icon>
                <ng-template #suggestionIconFallback>
                  <span class="item-icon-text">?</span>
                </ng-template>
              </div>
              <div class="item-content">
                <div class="item-subtitle" *ngIf="suggestion.reason">{{ suggestion.reason }}</div>
                <div class="item-title">{{ suggestion.text }}</div>
              </div>
              <div class="item-action">
                <span class="action-icon">Search</span>
              </div>
            </div>
          </div>
        </div>

        <!-- 💬 AI Help Section -->
        <div class="results-section ai-help-section" *ngIf="results.aiHelp">
          <div class="section-header">
            <span class="section-icon">💬</span>
            <span class="section-title">AI Can Help</span>
          </div>
          <div class="section-items">
            <div class="ai-help-card">
              <div class="ai-help-message">{{ results.aiHelp.message }}</div>

              <!-- Workflow Preview -->
              <div class="workflow-preview" *ngIf="results.aiHelp.workflowPreview && results.aiHelp.workflowPreview.length > 0">
                <div class="preview-steps">
                  <div *ngFor="let step of results.aiHelp.workflowPreview; let i = index" class="preview-step">
                    {{ i + 1 }}. {{ step }}
                  </div>
                </div>
              </div>

              <!-- Action Buttons -->
              <div class="ai-help-actions">
                <button
                  *ngFor="let action of results.aiHelp.actions; let i = index"
                  class="ai-action-btn"
                  [class.primary]="i === 0"
                  [class.selected]="selectedSection === 'ai-help' && i === selectedIndex"
                  (click)="executeAIAction(action)"
                  (mouseenter)="selectItem('ai-help', i)"
                >
                  <span class="action-btn-icon">{{ action.icon }}</span>
                  <span class="action-btn-label">{{ action.label }}</span>
                  <span class="action-btn-info" *ngIf="results.aiHelp.workflowSteps">
                    ({{ results.aiHelp.workflowSteps }} steps)
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- 🎥 Videos Section -->
        <div class="results-section" *ngIf="results.videos.length > 0">
          <div class="section-header">
            <span class="section-icon">🎥</span>
            <span class="section-title">Video Tutorials</span>
            <span class="section-count">({{ results.videos.length }})</span>
          </div>
          <div class="section-items">
            <div
              *ngFor="let video of results.videos; let i = index; trackBy: trackById"
              class="palette-item video-item"
              [class.selected]="selectedSection === 'videos' && i === selectedIndex"
              [class.expanded]="expandedVideoId === video.id"
              (click)="playVideo(video)"
              (mouseenter)="selectItem('videos', i)"
            >
              <div class="item-icon">▶</div>
              <div class="item-content">
                <div class="item-title">{{ video.title }}</div>
                <div class="item-subtitle">
                  <span class="video-duration">{{ video.duration }}</span>
                  <span *ngIf="video.description"> • {{ video.description }}</span>
                </div>

                <!-- Expanded Video Player -->
                <div class="video-player" *ngIf="expandedVideoId === video.id">
                  <!-- Placeholder when video isn't hosted yet -->
                  <ng-container *ngIf="video.isPlaceholder; else liveVideo">
                    <div class="video-placeholder">
                      <div class="placeholder-icon">🎬</div>
                      <div class="placeholder-text">Video tutorial available</div>
                      <div class="placeholder-file">{{ video.sourceFile }}</div>
                      <div class="placeholder-hint">Hosting coming soon</div>
                    </div>
                  </ng-container>
                  <ng-template #liveVideo>
                    <video
                      controls
                      autoplay
                      preload="metadata"
                      [src]="video.webVideoLink"
                      (click)="$event.stopPropagation()"
                    >
                      Your browser does not support the video tag.
                    </video>
                  </ng-template>

                  <!-- Try It Now Button -->
                  <div class="video-actions" *ngIf="video.relatedAction">
                    <button class="btn-try-now" (click)="executeQuickAction(video.relatedAction); $event.stopPropagation()">
                      📤 {{ video.relatedAction.label }}
                    </button>
                  </div>
                </div>
              </div>
              <div class="item-action">
                <span class="action-icon" *ngIf="expandedVideoId !== video.id">Watch</span>
                <span class="action-icon" *ngIf="expandedVideoId === video.id">Hide</span>
              </div>
            </div>
          </div>
        </div>

        <!-- 📖 Knowledge Base Section (RAG from support.md) -->
        <div class="results-section knowledge-section" *ngIf="results.knowledge || isKnowledgeLoading">
          <div class="section-header">
            <span class="section-icon">📖</span>
            <span class="section-title">How-to Guide</span>
          </div>
          <!-- Loading state while RAG is fetching -->
          <div class="knowledge-card knowledge-loading" *ngIf="isKnowledgeLoading && !results.knowledge">
            <div class="knowledge-loading-dots">
              <span></span><span></span><span></span>
            </div>
            <div class="knowledge-loading-text">Looking up help article...</div>
          </div>
          <!-- RAG answer -->
          <div class="knowledge-card" *ngIf="results.knowledge">
            <div class="knowledge-answer">{{ results.knowledge.answer }}</div>
          </div>
        </div>

        <!-- Empty State -->
        <div class="palette-empty" *ngIf="!hasResults && query && !isTrialMode">
          <div class="empty-icon">🔍</div>
          <div class="empty-title">No results found</div>
          <div class="empty-subtitle">
            Try a different search or
            <button class="link-button" (click)="openSupportChat()">chat with support</button>
          </div>
        </div>

      </div>
      <!-- End Left Pane: Main Content -->

      <!-- Right Pane: Quick Actions Sidebar -->
      <div class="palette-sidebar">
        <div class="sidebar-header">
          <span class="sidebar-icon">⚡</span>
          <span class="sidebar-title">Quick Actions</span>
        </div>

        <div class="sidebar-actions">
          <button
            *ngFor="let action of results.quickActions; let i = index; trackBy: trackById"
            class="sidebar-action-item"
            [class.selected]="selectedSection === 'quick-actions' && i === selectedIndex"
            (click)="executeQuickAction(action)"
            (mouseenter)="selectItem('quick-actions', i)"
          >
            <span class="action-icon-large">{{ action.icon }}</span>
            <span class="action-label">{{ action.title }}</span>
          </button>
        </div>
      </div>
      <!-- End Right Pane: Quick Actions Sidebar -->

    </div>
    <!-- End 2-Pane Layout -->

    <!-- Footer -->
    <div class="palette-footer">
      <div class="footer-left">
        <div class="footer-hints">
          <span class="hint">
            <kbd>↑</kbd> <kbd>↓</kbd> Navigate
          </span>
          <span class="hint">
            <kbd>Enter</kbd> Select
          </span>
          <span class="hint">
            <kbd>→</kbd> Expand Video
          </span>
          <span class="hint">
            <kbd>Esc</kbd> Close
          </span>
        </div>
      </div>
    </div>

  </div>
</div>
